import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AuthUserContext } from '../Session';
import { FirebaseContext } from '../Firebase';
import { useProjectSelection } from '../ProjectSelection';
import { useShapesAsTasks } from './useShapesAsTasks';
import { useHistory } from '../Replan/hooks/useHistory';
import { getShapeMeasurementData } from '../Replan/utils/measurementUtils';
import {
  getDisplayMeasurement,
  getDisplayAreaMeasurement,
  resolveShapeScaleProps,
} from '../Replan/utils/drawingUtils';
import { TOOLS } from '../Replan/constants/drawingTools';
import { METRIC_SCALES, IMPERIAL_SCALES } from '../Replan/constants/scales';
import ReactDOM from 'react-dom';
import './GanttPlanning.css';

// =============================================================================
// Helpers
// =============================================================================

const uid = () =>
  `t_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

/** YYYY-MM-DD string from a date (local time) */
const dateStr = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

/** Parse YYYY-MM-DD or ISO string into a local Date object, avoiding UTC shifts */
const parseDateStr = (s) => {
  if (!s) return null;
  if (s instanceof Date) return s;

  // Try YYYY-MM-DD first
  const match = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const y = parseInt(match[1], 10);
    const m = parseInt(match[2], 10) - 1;
    const d = parseInt(match[3], 10);
    return new Date(y, m, d);
  }

  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt;
};

/** Human-readable date for display */
const formatDateForDisplay = (d, locale = 'en-US') => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/** Deep-clone a task (dates, deps, styles) */
const cloneTask = (t) => ({
  ...t,
  start: new Date(t.start),
  end: new Date(t.end),
  dependencies: [...(t.dependencies || [])],
  styles: t.styles ? { ...t.styles } : {},
});

const LOCALE_MAP = { en: 'en-GB', fr: 'fr-FR', es: 'es-ES' };

// =============================================================================
// Firestore serialisation helpers
// =============================================================================

const fromFirestore = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.map((t, idx) => ({
    ...t,
    start: parseDateStr(t.start?.toDate ? t.start.toDate() : t.start) || new Date(),
    end: parseDateStr(t.end?.toDate ? t.end.toDate() : t.end) || new Date(),
    progress: t.progress ?? 0,
    type: t.type || 'task',
    project: t.project || '',
    dependencies: t.dependencies || [],
    hideChildren: t.hideChildren ?? false,
    isDisabled: t.isDisabled ?? false,
    styles: t.styles || {},
    displayOrder: t.displayOrder ?? idx,
  }));
};

const toFirestore = (arr) =>
  arr.map((t, idx) => ({
    id: t.id,
    name: t.name,
    start: dateStr(t.start),
    end: dateStr(t.end),
    progress: t.progress ?? 0,
    type: t.type || 'task',
    project: t.project || '',
    dependencies: t.dependencies || [],
    hideChildren: t.hideChildren ?? false,
    isDisabled: t.isDisabled ?? false,
    styles: t.styles || {},
    displayOrder: idx,
  }));

// =============================================================================
// Pure data helpers
// =============================================================================

/**
 * Auto-compute project (group) progress as the average of its children.
 * Also auto-compute project start/end from children date ranges.
 */
const computeProjectAggregates = (tasks) =>
  tasks.map((task) => {
    if (task.type !== 'project') return task;
    const children = tasks.filter((t) => t.project === task.id);
    if (children.length === 0) return task;

    const avgProgress = Math.round(
      children.reduce((s, c) => s + (c.progress || 0), 0) / children.length,
    );

    // Strictly derived from children.
    const start = new Date(Math.min(...children.map((c) => c.start.getTime())));
    const end = new Date(Math.max(...children.map((c) => c.end.getTime())));

    return { ...task, progress: avgProgress, start, end };
  });

/**
 * Sort tasks so project groups come first, followed by their children,
 * then orphan tasks/milestones. Respects displayOrder within each bucket.
 */
const sortTasks = (tasks) => {
  const sorted = [];
  const placed = new Set();

  const projectItems = tasks
    .filter((t) => t.type === 'project')
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  projectItems.forEach((proj) => {
    sorted.push(proj);
    placed.add(proj.id);

    tasks
      .filter((t) => t.project === proj.id)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .forEach((child) => {
        sorted.push(child);
        placed.add(child.id);
      });
  });

  tasks
    .filter((t) => !placed.has(t.id))
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .forEach((t) => sorted.push(t));

  return sorted;
};

/**
 * Build the final displayTasks array with:
 *  - auto-computed project progress & date ranges
 *  - proper sort order
 *  - displayOrder assigned sequentially for gantt-task-react
 *  - children of collapsed groups filtered out
 */
const buildDisplayTasks = (tasks) => {
  const aggregated = computeProjectAggregates(tasks);
  const sorted = sortTasks(aggregated);

  const collapsedProjects = new Set(
    sorted.filter((t) => t.type === 'project' && t.hideChildren).map((t) => t.id),
  );

  const visible = sorted.filter(
    (t) => !t.project || !collapsedProjects.has(t.project),
  );

  return visible.map((t, i) => ({ ...t, displayOrder: i + 1 }));
};

/**
 * Cascade dependent task dates forward when a task's end date moves later.
 * Uses iterative BFS to avoid recursion issues.
 */
const cascadeDependents = (tasks, changedId) => {
  const result = tasks.map(cloneTask);
  const queue = [changedId];
  const visited = new Set();

  while (queue.length > 0) {
    const parentId = queue.shift();
    if (visited.has(parentId)) continue;
    visited.add(parentId);

    const parent = result.find((t) => t.id === parentId);
    if (!parent) continue;

    result.forEach((dep) => {
      if (!dep.dependencies?.includes(parentId)) return;
      if (dep.start < parent.end) {
        const duration = dep.end.getTime() - dep.start.getTime();
        dep.start = new Date(parent.end);
        dep.end = new Date(dep.start.getTime() + duration);
        queue.push(dep.id);
      }
    });
  }

  return result;
};

/**
 * Detect if adding a dependency would create a circular loop.
 * @param {Array} tasks - Current task list
 * @param {String} taskId - The task being updated
 * @param {String} potentialDepId - The ID of the task to be added as a dependency
 * @returns {Boolean} - True if a cycle would be created
 */
const hasCycle = (tasks, taskId, potentialDepId) => {
  if (taskId === potentialDepId) return true;
  const queue = [potentialDepId];
  const visited = new Set();
  while (queue.length > 0) {
    const currentId = queue.shift();
    if (currentId === taskId) return true;
    if (visited.has(currentId)) continue;
    visited.add(currentId);
    const currentTask = tasks.find((t) => String(t.id) === String(currentId));
    if (currentTask && currentTask.dependencies) {
      queue.push(...currentTask.dependencies);
    }
  }
  return false;
};

// =============================================================================
// Sub-components
// =============================================================================

// Dependency picker is now integrated directly into RowDetailsPicker

// --- Row Details Picker (dropdown menu for task details) -------------------------

const RowDetailsPicker = ({
  task,
  allTasks,
  onChangeType,
  onChangeDeps,
  onDuplicate,
  onDelete,
  onClose,
  editingCell,
  setEditingCell,
  editValue,
  setEditValue,
  inputRef,
  commitAndClose,
  handleKeyDown,
  t,
}) => {
  const ref = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const isProject = task.type === 'project';
  const isMilestone = task.type === 'milestone';
  const depNames = (task.dependencies || [])
    .map((id) => allTasks.find((x) => x.id === id)?.name)
    .filter(Boolean);

  // Position the dropdown relative to the button
  useEffect(() => {
    const button = document.querySelector(`.gantt-inline-btn--menu[data-task-id="${window.CSS.escape(task.id)}"]`);
    if (button) {
      const rect = button.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX - 200,
      });
    }
  }, [task.id]);

  useEffect(() => {
    const onClick = (e) => {
      // Use composedPath to robustly determine whether the click originated
      // from inside the picker or the originating menu button. This works
      // correctly across shadow DOM/React updates and when the DOM is
      // re-rendered during the event.
      const path = (e.composedPath && e.composedPath()) || e.path || [];
      const menuBtn = document.querySelector(`.gantt-inline-btn--menu[data-task-id="${window.CSS.escape(task.id)}"]`);

      const clickedInside = ref.current && path.includes(ref.current);
      const clickedMenuBtn = menuBtn && path.includes(menuBtn);

      if (!clickedInside && !clickedMenuBtn) onClose();
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [onClose, task.id]);

  const content = (
    <div
      className="gantt-row-details-picker"
      ref={ref}
      style={{ top: `${position.top}px`, left: `${position.left}px`, position: 'absolute' }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Type */}
      <div className="gantt-row-details-section">
        <label className="gantt-row-details-label">
          {t('gantt.col_type', { defaultValue: 'Type' })}
        </label>
        <select
          className="gantt-row-details-select"
          value={task.type}
          onChange={(e) => {
            e.stopPropagation();
            onChangeType(task.id, e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="task">{t('gantt.type_task', { defaultValue: 'Task' })}</option>
          <option value="project">{t('gantt.type_project', { defaultValue: 'Group' })}</option>
          <option value="milestone">{t('gantt.type_milestone_short', { defaultValue: 'Mile.' })}</option>
        </select>
      </div>

      {/* Progress */}
      {!isProject && (
        <div className="gantt-row-details-section">
          <label className="gantt-row-details-label">
            {t('gantt.col_progress', { defaultValue: 'Progress' })} %
          </label>
          {editingCell?.taskId === task.id && editingCell?.field === 'progress' ? (
            <input
              ref={inputRef}
              type="number"
              min="0"
              max="100"
              className="gantt-row-details-input gantt-row-details-input--num"
              value={editValue}
              onChange={(e) => {
                e.stopPropagation();
                setEditValue(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              onBlur={commitAndClose}
              autoFocus
            />
          ) : (
            <div
              className="gantt-row-details-progress"
              onClick={(e) => {
                e.stopPropagation();
                setEditingCell({ taskId: task.id, field: 'progress' });
                setEditValue(String(task.progress));
              }}
            >
              <div className="gantt-progress-bar">
                <div
                  className="gantt-progress-fill"
                  style={{ width: `${task.progress}%` }}
                />
              </div>
              <span className="gantt-progress-text">{task.progress}</span>
            </div>
          )}
        </div>
      )}

      {/* Dependencies */}
      <div className="gantt-row-details-section">
        <label className="gantt-row-details-label">
          {t('gantt.col_deps', { defaultValue: 'Dependencies' })}
        </label>
        <div className="gantt-row-details-deps" onClick={(e) => e.stopPropagation()}>
          {allTasks.filter((x) => x.id !== task.id).length === 0 ? (
            <div className="gantt-dep-picker-empty">{t('gantt.no_other_tasks', { defaultValue: 'No other tasks' })}</div>
          ) : (
            <div className="gantt-dep-picker-list">
              {allTasks
                .filter((c) => c.id !== task.id)
                .map((c) => {
                  const isChecked = (task.dependencies || []).includes(c.id);
                  return (
                    <label key={c.id} className="gantt-dep-picker-item" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          e.stopPropagation();
                          const next = new Set(task.dependencies || []);
                          if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                          onChangeDeps(task.id, [...next]);
                        }}
                      />
                      <span className={`gantt-dep-type-icon gantt-dep-type-icon--${c.type}`}>
                        {c.type === 'milestone' ? '\u25C6' : c.type === 'project' ? '\u25A4' : '\u25AC'}
                      </span>
                      <span className="gantt-dep-picker-name">{c.name}</span>
                    </label>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="gantt-row-details-section gantt-row-details-section--actions">
        <button
          className="gantt-row-details-btn gantt-row-details-btn--dup"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate(task);
            onClose();
          }}
          title={t('gantt.duplicate', { defaultValue: 'Duplicate' })}
        >
          {'\u29C9'} {t('gantt.duplicate', { defaultValue: 'Duplicate' })}
        </button>
        <button
          className="gantt-row-details-btn gantt-row-details-btn--del"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(task);
            onClose();
          }}
          title={t('gantt.delete', { defaultValue: 'Delete' })}
        >
          {'\u2715'} {t('gantt.delete', { defaultValue: 'Delete' })}
        </button>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(content, document.body);
  }

  return content;
};

// --- Custom Tooltip -----------------------------------------------------------
// Module-level flag for tooltip visibility to avoid prop changes causing re-renders
let tooltipEnabled = true;

// Simple non-reactive tooltip to avoid infinite re-render issues with gantt-task-react
const ControlledTooltip = React.memo(({ task }) => {
  if (!task || !tooltipEnabled) return null;
  const duration = Math.max(
    1,
    Math.round(
      (task.end.getTime() - task.start.getTime()) / (1000 * 60 * 60 * 24),
    ),
  );
  return (
    <div className="gantt-tooltip">
      <b className="gantt-tooltip-name">{task.name}</b>
      <div className="gantt-tooltip-row">
        <span className="gantt-tooltip-label">Start</span>
        <span>{task.start.toLocaleDateString()}</span>
      </div>
      <div className="gantt-tooltip-row">
        <span className="gantt-tooltip-label">End</span>
        <span>{task.end.toLocaleDateString()}</span>
      </div>
      <div className="gantt-tooltip-row">
        <span className="gantt-tooltip-label">Duration</span>
        <span>{duration}d</span>
      </div>
      <div className="gantt-tooltip-row">
        <span className="gantt-tooltip-label">Progress</span>
        <span>{task.progress}%</span>
      </div>
      {task.dependencies?.length > 0 && (
        <div className="gantt-tooltip-row">
          <span className="gantt-tooltip-label">Dependencies</span>
          <span>{task.dependencies.length} task(s)</span>
        </div>
      )}
      <div className="gantt-tooltip-row">
        <span className="gantt-tooltip-label">Type</span>
        <span className="gantt-tooltip-type">{task.type}</span>
      </div>
    </div>
  );
});

// --- Custom TaskListHeader ----------------------------------------------------

const GanttTaskListHeader = ({
  headerHeight,
  rowWidth,
  fontFamily,
  fontSize,
  onAddTask,
  t,
}) => (
  <div
    className="gantt-table-header"
    style={{ '--header-height': `${headerHeight}px`, '--row-width': `${rowWidth}px` }}
  >
    <div className="gantt-table-header-cell gantt-table-header-cell--name">
      {t('gantt.col_name', { defaultValue: 'Name' })}
    </div>
    <div className="gantt-table-header-cell gantt-table-header-cell--date">
      {t('gantt.col_from', { defaultValue: 'From' })}
    </div>
    <div className="gantt-table-header-cell gantt-table-header-cell--date">
      {t('gantt.col_to', { defaultValue: 'To' })}
    </div>
    <div className="gantt-table-header-cell gantt-table-header-cell--qty">
      {t('gantt.col_qty', { defaultValue: 'Quantity' })}
    </div>
    <div className="gantt-table-header-cell gantt-table-header-cell--menu">
      <button
        className="gantt-inline-btn gantt-inline-btn--add"
        onClick={onAddTask}
        title={t('gantt.add_task', { defaultValue: 'Add Task' })}
      >
        +
      </button>
    </div>
  </div>
);

// --- Sortable Row (each row is a draggable item) -----------------------------

const SortableRow = ({
  task,
  rowHeight,
  rowWidth,
  selectedTaskId,
  setSelectedTask,
  editingCell,
  editValue,
  setEditValue,
  setEditingCell,
  inputRef,
  commitAndClose,
  handleKeyDown,
  onChangeType,
  onDeleteTask,
  onDuplicateTask,
  onChangeDeps,
  onCommitEdit,

  allTasks,
  isDropTarget,
  onExpanderClick,
  expandedRowId,
  setExpandedRowId,
  t,
  i18n,
}) => {
  console.log(`[Gantt] Rendering row for task ${task.id}, name: ${task.name}, project: ${task.project}`);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    data: { type: task.type, project: task.project },
  });

  const style = {
    height: rowHeight,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  const isProject = task.type === 'project';
  const isMilestone = task.type === 'milestone';
  const depNames = (task.dependencies || [])
    .map((id) => allTasks.find((x) => x.id === id)?.name)
    .filter(Boolean);
  const isExpanded = expandedRowId === task.id;

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={[
          'gantt-table-row',
          selectedTaskId === task.id && 'gantt-table-row--selected',
          isProject && 'gantt-table-row--project',
          task.project && 'gantt-row--child',
          isDragging && 'gantt-table-row--dragging',
          isDropTarget && 'gantt-table-row--drop-target',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => setSelectedTask(task.id)}
      >
        {/* Name (with toggle for project) */}
        <div className={["gantt-table-cell", "gantt-table-cell--name", editingCell?.taskId === task.id && editingCell?.field === 'name' && "gantt-table-cell--editing", task.project && 'gantt-cell--child'].filter(Boolean).join(' ')}
          {...(editingCell?.taskId === task.id && editingCell?.field === 'name' ? {} : attributes)}
          {...(editingCell?.taskId === task.id && editingCell?.field === 'name' ? {} : listeners)}
        >
          {isProject && (
            <span
              className="gantt-expander"
              onClick={(e) => {
                e.stopPropagation();
                onExpanderClick(task);
              }}
              title={task.hideChildren ? t('gantt.expand_group', { defaultValue: 'Expand group' }) : t('gantt.collapse_group', { defaultValue: 'Collapse group' })}
            >
              {task.hideChildren ? '\u25B8' : '\u25BE'}
            </span>
          )}
          {task.type === 'milestone' && (
            <span className="gantt-milestone-icon">{'\u25C6'}</span>
          )}

          {editingCell?.taskId === task.id && editingCell?.field === 'name' ? (
            <input
              ref={inputRef}
              className="gantt-inline-input"
              value={editValue}
              onChange={(e) => {
                e.stopPropagation();
                setEditValue(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              onBlur={commitAndClose}
              autoFocus
            />
          ) : (
            <span
              className="gantt-cell-text"
              title={task.name}
              style={{
                color: isProject ? 'var(--pico-h1-color)' : (task.project || (task.shapes && task.shapes.length > 0)) ? 'var(--pico-muted-color)' : 'var(--pico-color)',
                fontSize: (isProject) ? '0.9rem' : (task.project || (task.shapes && task.shapes.length > 0)) ? '0.8rem' : '0.85rem',
                fontWeight: (isProject) ? '800' : (task.project || (task.shapes && task.shapes.length > 0)) ? '500' : '700',
                marginLeft: (task.project || (task.shapes && task.shapes.length > 0)) ? '1.2rem' : '0',
                display: 'inline-flex',
                alignItems: 'center'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setEditingCell({ taskId: task.id, field: 'name' });
                setEditValue(task.name);
              }}
            >
              {(task.project || (task.shapes && task.shapes.length > 0)) && <span className="gantt-cell-child-indicator">↳</span>}
              <span className="gantt-text-truncate-wrapper">
                {(!task.name || task.name.length <= 25) ? (
                  task.name
                ) : (
                  <>
                    <span className="gantt-text-prefix">{task.name.slice(0, 5)}</span>
                    {/* <span className="gantt-text-spacer">-</span> */}
                    <span className="gantt-text-suffix">{task.name}</span>
                  </>
                )}
              </span>
            </span>
          )}
        </div>

        {/* From */}
        <div className="gantt-table-cell gantt-table-cell--date">
          {editingCell?.taskId === task.id && editingCell?.field === 'start' ? (
            <input
              ref={inputRef}
              type="date"
              className="gantt-inline-input gantt-inline-input--date"
              value={editValue}
              onChange={(e) => {
                e.stopPropagation();
                const newVal = e.target.value;
                setEditValue(newVal);
                // For date inputs, commit immediately on valid change
                if (newVal && newVal.length === 10) {
                  onCommitEdit(task.id, 'start', newVal);
                }
              }}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              onBlur={commitAndClose}
              autoFocus
            />
          ) : (
            <span
              className="gantt-cell-text"
              onClick={(e) => {
                e.stopPropagation();
                setEditingCell({ taskId: task.id, field: 'start' });
                setEditValue(dateStr(task.start));
              }}
            >
              {formatDateForDisplay(task.start, i18n.language)}
            </span>
          )}
        </div>

        {/* To */}
        <div className="gantt-table-cell gantt-table-cell--date">
          {editingCell?.taskId === task.id && editingCell?.field === 'end' ? (
            <input
              ref={inputRef}
              type="date"
              className="gantt-inline-input gantt-inline-input--date"
              value={editValue}
              onChange={(e) => {
                e.stopPropagation();
                const newVal = e.target.value;
                setEditValue(newVal);
                // For date inputs, we often want to commit immediately on valid change
                if (newVal && newVal.length === 10) {
                  onCommitEdit(task.id, 'end', newVal);
                }
              }}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              onBlur={commitAndClose}
              autoFocus
            />
          ) : (
            <span
              className="gantt-cell-text"
              onClick={(e) => {
                e.stopPropagation();
                setEditingCell({ taskId: task.id, field: 'end' });
                setEditValue(dateStr(task.end));
              }}
            >
              {formatDateForDisplay(task.end, i18n.language)}
            </span>
          )}
        </div>

        {/* Quantity (ReadOnly for shapes) */}
        <div className="gantt-table-cell gantt-table-cell--qty" style={{ fontSize: '0.8em', color: 'var(--pico-muted-color)' }}>
          <span className="gantt-cell-text" title={task.quantity || ''}>
            {task.quantity || ''}
          </span>
        </div>

        {/* Menu Button */}
        <div className="gantt-table-cell gantt-table-cell--menu" style={{ position: 'relative' }}>
          <button
            data-task-id={task.id}
            className="gantt-inline-btn gantt-inline-btn--menu"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedRowId(isExpanded ? null : task.id);
            }}
            title={t('gantt.more_options', { defaultValue: 'More options' })}
          >
            {'\u2807'}
          </button>

          {/* Expanded Details Dropdown Menu */}
          {isExpanded && (
            <RowDetailsPicker
              task={task}
              allTasks={allTasks}
              onChangeType={onChangeType}
              onChangeDeps={onChangeDeps}
              onDuplicate={onDuplicateTask}
              onDelete={onDeleteTask}
              onClose={() => setExpandedRowId(null)}
              editingCell={editingCell}
              setEditingCell={setEditingCell}
              editValue={editValue}
              setEditValue={setEditValue}
              inputRef={inputRef}
              commitAndClose={commitAndClose}
              handleKeyDown={handleKeyDown}
              t={t}
            />
          )}
        </div>
      </div>
    </>
  );
};

const GanttTaskListTable = ({
  // Props from gantt-task-react
  rowHeight,
  rowWidth,
  fontFamily,
  fontSize,
  tasks: displayedTasks,
  selectedTaskId,
  setSelectedTask,
  onExpanderClick,
  // Custom props passed from parent
  onDeleteTask,
  onDuplicateTask,
  onChangeType,
  onChangeDeps,
  allTasks,
  editingCell,
  setEditingCell,
  editValue,
  setEditValue,
  onCommitEdit,

  onReorder,
  onDropIntoGroup,
  expandedRowId,
  setExpandedRowId,
  t,
  i18n,
}) => {
  const inputRef = useRef(null);
  const [overGroupId, setOverGroupId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const overGroupRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [editingCell]);



  const commitAndClose = useCallback(() => {
    onCommitEdit();
    setEditingCell(null);
  }, [onCommitEdit, setEditingCell]);

  const handleKeyDown = useCallback(
    (e) => {
      e.stopPropagation(); // Prevent parent handlers from interfering
      if (e.key === 'Enter') {
        e.preventDefault();
        commitAndClose();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditingCell(null);
      }
      // For all other keys, allow default behavior (typing)
    },
    [commitAndClose, setEditingCell],
  );

  const groupAwareCollision = useCallback((args) => {
    const pointerHits = pointerWithin(args);
    const projectHit = pointerHits.find(
      (c) =>
        c.data?.droppableContainer?.data?.current?.type === 'project',
    );
    if (projectHit) return [projectHit];
    return closestCenter(args);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const taskIds = useMemo(
    () => displayedTasks.map((t) => t.id),
    [displayedTasks],
  );

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    overGroupRef.current = null;
  }, []);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setOverGroupId(null);
      overGroupRef.current = null;
      return;
    }
    const overType = over.data?.current?.type;
    const activeType = active.data?.current?.type;
    const overProject = over.data?.current?.project;

    // Hovering over a project group
    if (overType === 'project' && activeType !== 'project') {
      setOverGroupId(over.id);
      overGroupRef.current = over.id;
    }
    // Hovering over a child task of a group -> highlight parent group
    else if (overProject && activeType !== 'project') {
      setOverGroupId(overProject);
      overGroupRef.current = overProject;
    }
    else {
      setOverGroupId(null);
      overGroupRef.current = null;
    }
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      const pendingGroup = overGroupRef.current;
      setOverGroupId(null);
      setActiveId(null);
      overGroupRef.current = null;

      if (!active || !over || active.id === over.id) return;

      const overType = over?.data?.current?.type;
      const activeType = active.data?.current?.type;
      const overProject = over?.data?.current?.project;

      // Drop directly on a project group -> add to group
      if (overType === 'project' && activeType !== 'project') {
        onDropIntoGroup(active.id, over.id);
        return;
      }

      // Drop on a task that belongs to a group -> add to that group first
      if (overProject && activeType !== 'project') {
        // First add to group
        onDropIntoGroup(active.id, overProject);
        // Then reorder (using setTimeout to allow state to update)
        // The reorder will happen based on the displayed tasks after grouping
        return;
      }

      if (pendingGroup && activeType !== 'project') {
        onDropIntoGroup(active.id, pendingGroup);
        return;
      }

      // Regular reorder
      const oldIndex = displayedTasks.findIndex(
        (t) => t.id === active.id,
      );
      const newIndex = displayedTasks.findIndex(
        (t) => t.id === over.id,
      );
      if (oldIndex === -1 || newIndex === -1) return;
      onReorder(oldIndex, newIndex);
    },
    [displayedTasks, allTasks, onReorder, onDropIntoGroup],
  );

  const handleDragCancel = useCallback(() => {
    setOverGroupId(null);
    setActiveId(null);
    overGroupRef.current = null;
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={groupAwareCollision}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={taskIds}
        strategy={verticalListSortingStrategy}
      >
        <div
          className="gantt-table"
          style={{ '--row-width': `${rowWidth}px`, '--row-height': `${rowHeight}px` }}
        >
          {displayedTasks.map((task) => {
            const isDropTarget =
              task.type === 'project' &&
              overGroupId === task.id &&
              activeId !== task.id;

            return (
              <SortableRow
                key={task.id}
                task={task}
                rowHeight={rowHeight}
                rowWidth={rowWidth}
                selectedTaskId={selectedTaskId}
                setSelectedTask={setSelectedTask}
                editingCell={editingCell}
                editValue={editValue}
                setEditValue={setEditValue}
                setEditingCell={setEditingCell}
                inputRef={inputRef}
                commitAndClose={commitAndClose}
                handleKeyDown={handleKeyDown}
                onChangeType={onChangeType}
                onDeleteTask={onDeleteTask}
                onDuplicateTask={onDuplicateTask}
                onChangeDeps={onChangeDeps}
                onCommitEdit={onCommitEdit}
                allTasks={allTasks}
                isDropTarget={isDropTarget}
                onExpanderClick={onExpanderClick}
                expandedRowId={expandedRowId}
                setExpandedRowId={setExpandedRowId}
                t={t}
                i18n={i18n}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};

// =============================================================================
// Main Component
// =============================================================================

const GanttPlanning = () => {
  const { t, i18n } = useTranslation();
  const authUser = useContext(AuthUserContext);
  const firebase = useContext(FirebaseContext);
  const {
    projects: availableProjects,
    selectedProject: globalSelectedProject,
    setSelectedProject: setGlobalSelectedProject,
  } = useProjectSelection();

  // -- State & History --------------------------------------------------------
  const {
    state: tasks,
    setState: setTasksWithHistory,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useHistory([], { debounceMs: 1000 });

  const tasksRef = useRef([]);
  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  const [viewMode, setViewMode] = useState(ViewMode.Day);
  const [selectedProject, setSelectedProjectState] = useState(globalSelectedProject || '');
  const [orgFromProject, setOrgFromProject] = useState(null);
  const organization = useMemo(() => {
    if (orgFromProject) return orgFromProject;
    if (!authUser?.roles) return null;
    const orgs = Object.keys(authUser.roles).filter((k) => authUser.roles[k] !== null);
    return orgs[0] || null;
  }, [authUser, orgFromProject]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showTaskList, setShowTaskList] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [viewDate, setViewDate] = useState(new Date());
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedShapeGroups, setExpandedShapeGroups] = useState(new Set());
  const [stackIdentical, setStackIdentical] = useState(false);
  const [separateByPage, setSeparateByPage] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [showBaseline, setShowBaseline] = useState(false);
  const [projectAssemblies, setProjectAssemblies] = useState([]);

  // -- Shape Toolbar State --
  const [groupingKey, setGroupingKey] = useState('Assembly');
  const [isVersionDropdownOpen, setIsVersionDropdownOpen] = useState(false);
  const [versionDropdownPos, setVersionDropdownPos] = useState({ top: 0, left: 0 });
  const versionTriggerRef = useRef(null);
  const versionDropdownMenuRef = useRef(null);

  const handleToggleVersionDropdown = useCallback(() => {
    setIsVersionDropdownOpen((prev) => {
      if (!prev && versionTriggerRef.current) {
        const rect = versionTriggerRef.current.getBoundingClientRect();
        setVersionDropdownPos({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
      }
      return !prev;
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inTrigger = versionTriggerRef.current && versionTriggerRef.current.contains(e.target);
      const inMenu = versionDropdownMenuRef.current && versionDropdownMenuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) {
        setIsVersionDropdownOpen(false);
      }
    };
    if (isVersionDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVersionDropdownOpen]);

  const setSelectedProject = useCallback((nextProject) => {
    const next = nextProject || '';
    setSelectedProjectState(next);
    setGlobalSelectedProject(next);
  }, [setGlobalSelectedProject]);

  useEffect(() => {
    const nextGlobalProject = globalSelectedProject || '';
    if (nextGlobalProject !== selectedProject) {
      setSelectedProjectState(nextGlobalProject);
    }
  }, [globalSelectedProject, selectedProject]);

  // Try to resolve the project's parent organisation from Firestore
  useEffect(() => {
    let cancelled = false;
    const resolveOrg = async () => {
      setOrgFromProject(null);
      const projName = selectedProject || globalSelectedProject || '';
      if (!projName || !firebase) return;

      // First, check availableProjects for an organisation field
      try {
        if (Array.isArray(availableProjects) && availableProjects.length > 0) {
          const found = availableProjects.find(
            (p) => p.name === projName || p.id === projName || p.title === projName,
          );
          if (found && found.organization) {
            setOrgFromProject(found.organization);
            return;
          }
        }
      } catch (_e) {
        // ignore
      }

      // Otherwise, look through organizations the user belongs to and check each org's projects
      const userOrgs = Object.keys(authUser?.roles || {}).filter((k) => authUser.roles[k] !== null);
      for (const org of userOrgs) {
        if (cancelled) return;
        try {
          const projList = await firebase.doGetOrganizationProjects(org);
          if (Array.isArray(projList)) {
            const match = projList.find(
              (p) => p.id === projName || p.name === projName || p.title === projName,
            );
            if (match) {
              setOrgFromProject(org);
              return;
            }
          }
        } catch (_err) {
          // ignore and continue
        }
      }
      // not found
      setOrgFromProject(null);
    };

    resolveOrg();
    return () => { cancelled = true; };
  }, [firebase, selectedProject, globalSelectedProject, availableProjects, authUser]);

  // Fetch project assemblies when organization or selectedProject changes
  useEffect(() => {
    if (firebase && organization && selectedProject) {
      const fetchAssemblies = async () => {
        try {
          const querySnapshot = await firebase.doGetProjectAssemblies(
            organization,
            selectedProject,
          );
          const assemblies = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setProjectAssemblies(assemblies);
        } catch (error) {
          console.error('Error fetching project assemblies in Gantt:', error);
          setProjectAssemblies([]);
        }
      };
      fetchAssemblies();
    } else {
      setProjectAssemblies([]);
    }
  }, [firebase, organization, selectedProject]);

  // Sync module-level tooltip flag with component state
  useEffect(() => {
    tooltipEnabled = showTooltip;
  }, [showTooltip]);


  // -- Derived state ----------------------------------------------------------

  const {
    versions,
    selectedVersions,
    setSelectedVersions,
    shapes,
    loadingShapes,
  } = useShapesAsTasks(firebase, organization, selectedProject);

  const locale = useMemo(
    () => LOCALE_MAP[i18n.language] || 'en-GB',
    [i18n.language],
  );

  const columnWidth = useMemo(() => {
    switch (viewMode) {
      case ViewMode.Year:
        return 350;
      case ViewMode.Month:
        return 300;
      case ViewMode.Week:
        return 250;
      case ViewMode.Day:
        return 65;
      case ViewMode.HalfDay:
        return 60;
      case ViewMode.QuarterDay:
        return 55;
      case ViewMode.Hour:
        return 50;
      default:
        return 65;
    }
  }, [viewMode]);

  const allScales = useMemo(() => {
    return [...METRIC_SCALES, ...IMPERIAL_SCALES];
  }, []);

  const combinedTasks = useMemo(() => {
    // Default dates for shapes
    const shapeStart = new Date();
    const shapeEnd = new Date();
    shapeEnd.setDate(shapeEnd.getDate() + 5);

    const getAssemblyFriendlyName = (asmIdOrName) => {
      if (!asmIdOrName) return '';
      const matched = projectAssemblies.find(
        (a) => a.id === asmIdOrName || a.name === asmIdOrName
      );
      return matched ? (matched.name || matched.id) : asmIdOrName;
    };

    const getShapeStackKey = (shape) => {
      const markerSuffix = shape.type === TOOLS.POINT
        ? `|marker:${shape.pointMarker || 'disk'}`
        : '';
      let key = `${shape.type}|${shape.stroke || 'no-stroke'}${markerSuffix}`;

      if (separateByPage) {
        const pageId =
          shape?.pageInfo?.name ??
          shape?.pageInfo?.pageIndex ??
          shape?.pageInfo?.index ??
          'unknown-page';
        key += `|page:${pageId}`;
      }

      if (groupingKey) {
        const groupValue =
          groupingKey === '__page__'
            ? (shape?.pageInfo?.name ??
              shape?.pageInfo?.pageIndex ??
              shape?.pageInfo?.index ??
              'undefined')
            : (shape?.params?.[groupingKey] || 'Not Set');
        key += `|${groupingKey}:${groupValue}`;
      }
      return key;
    };

    const getMeasurementData = (shape) => {
      if (!shape.measurementContext) return null;
      const shapeScaleProps = resolveShapeScaleProps(
        shape,
        shape.measurementContext.currentScaleProps,
        allScales,
      );
      return getShapeMeasurementData(
        shape,
        shapeScaleProps,
        shape.measurementContext.dpi,
        shape.measurementContext.imageSize,
        shape.measurementContext.displayUnitSystem
      );
    };

    const getTypeLabel = (s) => {
      if (s.type === TOOLS.POINT && s.pointMarker && s.pointMarker !== 'disk') {
        return `${s.type} / ${s.pointMarker}`;
      }
      return s.type;
    };

    const getPageLabel = (s) => {
      return s.pageInfo?.name || (s.pageInfo?.pageIndex !== undefined ? `Page ${s.pageInfo.pageIndex + 1}` : 'Not Set');
    };

    // 1. Identify which shapes are valid and group them if needed
    const relevantGroupIds = new Set();
    const processedItems = [];

    if (stackIdentical) {
      const stacks = new Map();
      shapes.forEach((shape, index) => {
        const measurement = getMeasurementData(shape);
        if (!measurement || !measurement.summary || measurement.summary.includes(': 0 ') || measurement.summary === '0') {
          return;
        }

        const key = getShapeStackKey(shape);
        if (!stacks.has(key)) {
          stacks.set(key, { shapes: [], firstIndex: index });
        }
        stacks.get(key).shapes.push({ shape, measurement });
      });

      stacks.forEach((stack, key) => {
        const shapesInStack = stack.shapes;
        const repShape = shapesInStack[0].shape;

        let groupValue = repShape.params?.[groupingKey] || 'Not Set';
        if (groupingKey === '__page__') {
          groupValue = repShape.pageInfo?.name || (repShape.pageInfo?.pageIndex !== undefined ? `Page ${repShape.pageInfo.pageIndex + 1}` : 'Not Set');
        }
        const groupId = `group_${groupValue}`;
        relevantGroupIds.add(groupId);

        // Aggregate measurements with per-shape scale normalization
        const repScaleProps = resolveShapeScaleProps(
          repShape,
          repShape.measurementContext?.currentScaleProps,
          allScales,
        );
        const repRatio = repScaleProps?.ratio;
        const repIsArea =
          repShape.type === TOOLS.AREA;

        let totalValue = 0;
        let unit = '';
        let label = '';
        shapesInStack.forEach(({ shape, measurement }) => {
          if (!measurement.rawValue) return;
          const shapeScaleProps = resolveShapeScaleProps(
            shape,
            shape.measurementContext?.currentScaleProps,
            allScales,
          );
          const shapeRatio = shapeScaleProps?.ratio;
          const linearFactor = (repRatio && shapeRatio) ? shapeRatio / repRatio : 1;
          const conversionFactor = repIsArea ? linearFactor * linearFactor : linearFactor;
          totalValue += measurement.rawValue * conversionFactor;
          if (!unit) unit = measurement.rawUnit;
          if (!label) label = measurement.summary.split(':')[0];
        });

        const isArea = unit.includes('^2');
        const repCtx = repShape.measurementContext;
        const display = isArea
          ? getDisplayAreaMeasurement(totalValue, repScaleProps, repCtx?.dpi, repCtx?.displayUnitSystem)
          : getDisplayMeasurement(totalValue, repScaleProps, repCtx?.dpi, repCtx?.imageSize, repCtx?.displayUnitSystem);

        let qtyDisplay = `${label}: ${display.value} ${display.unit}`;
        // Override for Point/Marker
        if (repShape.type === TOOLS.POINT) {
          qtyDisplay = `Count: ${shapesInStack.length}`;
        }

        const assemblyName = getAssemblyFriendlyName(repShape.params?.Assembly);
        let baseName = assemblyName || repShape.params?.Reference || getTypeLabel(repShape);

        // If grouping by Assembly, show the Page name(s) instead
        if (groupingKey === 'Assembly') {
          const pageNames = new Set();
          shapesInStack.forEach(({ shape }) => pageNames.add(getPageLabel(shape)));
          baseName = Array.from(pageNames).sort().join(', ');
        }

        processedItems.push({
          id: `stack-${key}`,
          name: `${baseName} (x${shapesInStack.length})`,
          quantity: qtyDisplay,
          groupId,
          groupValue,
          displayOrder: stack.firstIndex + 10000,
          shapes: shapesInStack.map(s => s.shape)
        });
      });
    } else {
      shapes.forEach((shape, index) => {
        const measurement = getMeasurementData(shape);
        if (!measurement || !measurement.summary || measurement.summary.includes(': 0 ') || measurement.summary === '0') {
          return;
        }

        let groupValue = shape.params?.[groupingKey] || 'Not Set';
        if (groupingKey === '__page__') {
          groupValue = shape.pageInfo?.name || (shape.pageInfo?.pageIndex !== undefined ? `Page ${shape.pageInfo.pageIndex + 1}` : 'Not Set');
        }
        const groupId = `group_${groupValue}`;
        relevantGroupIds.add(groupId);

        const assemblyName = getAssemblyFriendlyName(shape.params?.Assembly);
        let baseName = assemblyName || shape.params?.Reference || getTypeLabel(shape);

        // If grouping by Assembly, show the Page name instead
        if (groupingKey === 'Assembly') {
          baseName = getPageLabel(shape);
        }

        let qtyDisplay = measurement.summary;
        if (shape.type === TOOLS.POINT) {
          qtyDisplay = 'Count: 1';
        }

        processedItems.push({
          id: String(shape.id),
          name: baseName,
          quantity: qtyDisplay,
          groupId,
          groupValue,
          displayOrder: index + 10000,
          shapes: [shape]
        });
      });
    }

    // 2. Filter tasks: keep non-virtual and relevant virtual groups (matching current groupingKey)
    const combined = tasks.filter(t =>
      !String(t.id).startsWith('group_') || (t.groupingSource === groupingKey) || relevantGroupIds.has(String(t.id))
    );

    const projectGroups = new Map();

    processedItems.forEach((item) => {
      const existingTask = combined.find(t => String(t.id) === item.id);

      if (groupingKey && !projectGroups.has(item.groupValue)) {
        const savedGroup = combined.find(t => String(t.id) === item.groupId);
        let groupName = item.groupValue;
        if (groupingKey === 'Assembly' && item.groupValue !== 'Not Set') {
          groupName = getAssemblyFriendlyName(item.groupValue);
        }
        projectGroups.set(item.groupValue, {
          id: item.groupId,
          name: groupName,
          start: savedGroup ? new Date(savedGroup.start) : shapeStart,
          end: savedGroup ? new Date(savedGroup.end) : shapeEnd,
          progress: 0,
          type: 'project',
          project: '',
          dependencies: [],
          hideChildren: !expandedShapeGroups.has(item.groupId),
          isDisabled: false,
          styles: {},
          groupingSource: groupingKey, // Mark the source for persistence
        });
      }

      if (existingTask) {
        const existingIndex = combined.indexOf(existingTask);
        combined[existingIndex] = {
          ...existingTask,
          quantity: item.quantity,
          project: groupingKey ? item.groupId : '',
          displayOrder: item.displayOrder
        };
      } else {
        combined.push({
          id: item.id,
          name: item.name,
          start: shapeStart,
          end: shapeEnd,
          progress: 0,
          type: 'task',
          project: groupingKey ? item.groupId : '',
          dependencies: [],
          hideChildren: false,
          isDisabled: false,
          styles: {},
          quantity: item.quantity,
          displayOrder: item.displayOrder
        });
      }
    });

    if (groupingKey) {
      projectGroups.forEach(proj => {
        const idx = combined.findIndex(t => String(t.id) === String(proj.id));
        if (idx === -1) {
          combined.push(proj);
        } else {
          combined[idx] = { ...combined[idx], ...proj };
        }
      });
    }

    // Force dynamic groups to respect state
    for (let i = 0; i < combined.length; i++) {
      if (combined[i].id && String(combined[i].id).startsWith('group_')) {
        combined[i] = {
          ...combined[i],
          hideChildren: !expandedShapeGroups.has(String(combined[i].id))
        };
      }
    }

    return combined;
  }, [tasks, shapes, groupingKey, expandedShapeGroups, stackIdentical, separateByPage, projectAssemblies]);

  const combinedTasksRef = useRef([]);
  useEffect(() => {
    combinedTasksRef.current = combinedTasks;
  }, [combinedTasks]);

  /** Display-ready task list for the <Gantt> component */
  const displayTasks = useMemo(() => buildDisplayTasks(combinedTasks), [combinedTasks]);

  // -- Load tasks -------------------------------------------------------------
  useEffect(() => {
    if (!firebase || !organization || !selectedProject) {
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const data = await firebase.doGetPlanningTasks(
          organization,
          selectedProject,
        );
        if (cancelled) return;
        const loadedTasks = data?.tasks ? fromFirestore(data.tasks) : [];
        setTasksWithHistory(loadedTasks, true); // Skip history for initial load
        setBaseline(data?.baseline ? fromFirestore(data.baseline) : null);
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setTasksWithHistory([], true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [firebase, organization, selectedProject, setTasksWithHistory]);

  // -- Persist ----------------------------------------------------------------
  const persist = useCallback(
    async (list) => {
      if (!firebase || !organization || !selectedProject) return;
      setSaving(true);
      try {
        await firebase.doSavePlanningTasks(
          organization,
          selectedProject,
          toFirestore(list),
          authUser?.uid,
          authUser?.email,
        );
      } catch (err) {
        console.error(err);
      } finally {
        setSaving(false);
      }
    },
    [firebase, organization, selectedProject, authUser],
  );

  const updateTasks = useCallback(
    (next) => {
      setTasksWithHistory(next);
      setHasUnsavedChanges(true);
    },
    [setTasksWithHistory],
  );

  const handleManualSave = useCallback(
    async () => {
      if (!hasUnsavedChanges) return;
      await persist(tasks);
      setHasUnsavedChanges(false);
    },
    [hasUnsavedChanges, persist, tasks],
  );

  const handleSaveBaseline = useCallback(async () => {
    if (!firebase || !organization || !selectedProject) return;
    if (!window.confirm(t('gantt.confirm_baseline', { defaultValue: 'Save current schedule as a baseline?' }))) return;

    setSaving(true);
    try {
      await firebase.doSavePlanningBaseline(
        organization,
        selectedProject,
        toFirestore(tasks)
      );
      setBaseline([...tasks]);
      alert(t('gantt.baseline_saved', { defaultValue: 'Baseline saved successfully.' }));
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }, [firebase, organization, selectedProject, tasks, t]);

  // Keyboard shortcut: Ctrl+S / Cmd+S to save
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasUnsavedChanges && !saving) {
          handleManualSave();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasUnsavedChanges, saving, handleManualSave]);

  // =========================================================================
  // gantt-task-react event handlers
  // =========================================================================

  /**
   * onDateChange(task, children)
   * Fires when user drags a bar to move/resize it.
   * The library passes the mutated task + its project children.
   * We cascade dependents forward if needed.
   */
  const handleDateChange = useCallback(
    (task, children) => {
      let next = [...tasksRef.current];

      const ensureTask = (srcTask) => {
        if (!srcTask) return;
        const idx = next.findIndex((t) => String(t.id) === String(srcTask.id));
        if (idx !== -1) {
          next[idx] = cloneTask(srcTask);
        } else {
          next.push(cloneTask(srcTask));
        }
      };

      if (task.type === 'project') {
        const originalGroup = combinedTasksRef.current.find(t => t.id === task.id);
        if (!originalGroup) return;

        // Calculate how much the group moved in total (delta)
        const delta = task.start.getTime() - originalGroup.start.getTime();

        // Find all tasks/shapes that belong to this group in the CURRENT display
        const childrenToShift = combinedTasksRef.current.filter(t => t.project === task.id);

        childrenToShift.forEach(child => {
          const updatedChild = cloneTask(child);
          const duration = child.end.getTime() - child.start.getTime();

          updatedChild.start = new Date(child.start.getTime() + delta);
          updatedChild.end = new Date(updatedChild.start.getTime() + duration);

          // If it's a stack, propagate to individual shapes
          if (String(child.id).startsWith('stack-')) {
            const src = combinedTasksRef.current.find(t => String(t.id) === String(child.id));
            if (src && src.shapes) {
              src.shapes.forEach(s => {
                const sub = { ...updatedChild, id: String(s.id), name: s.params?.Reference || s.type };
                ensureTask(sub);
                next = cascadeDependents(next, sub.id);
              });
            }
          }

          ensureTask(updatedChild);
          next = cascadeDependents(next, updatedChild.id);
        });

        // We DO NOT save the group itself to next (persistence), 
        // as its dates will be re-derived from the shifted children.
      } else {
        // Normal tasks
        if (String(task.id).startsWith('stack-')) {
          const src = combinedTasksRef.current.find(t => String(t.id) === String(task.id));
          if (src && src.shapes) {
            console.log(`[Gantt] Dragging stack ${task.id}, propagating dates to ${src.shapes.length} shapes`);
            src.shapes.forEach(s => {
              const subId = String(s.id);
              let sIdx = next.findIndex(t => String(t.id) === subId);
              if (sIdx === -1) {
                // If the individual shape task isn't in tasksRef yet, create it from the shape data
                const dummy = {
                  ...task,
                  id: subId,
                  name: s.params?.Reference || s.type,
                  start: new Date(task.start),
                  end: new Date(task.end)
                };
                next.push(dummy);
                sIdx = next.length - 1;
              } else {
                // If it exists, only update the dates and keep other metadata (like the name)
                const subClone = cloneTask(next[sIdx]);
                subClone.start = new Date(task.start);
                subClone.end = new Date(task.end);
                next[sIdx] = subClone;
              }
              // Cascade dependencies for each individual shape
              next = cascadeDependents(next, subId);
            });
          } else {
            console.warn(`[Gantt] Could not find stack source for ${task.id}`);
          }
        }
        ensureTask(task);
        if (children) {
          children.forEach(ensureTask);
        }
        next = cascadeDependents(next, task.id);
      }

      updateTasks(next);
    },
    [updateTasks],
  );

  /**
   * onProgressChange(task, children)
   * Fires when user drags the progress handle on a bar.
   */
  const handleProgressChange = useCallback(
    (task, _children) => {
      let next = [...tasksRef.current];
      const ensureTask = (srcTask) => {
        if (!srcTask) return;
        const idx = next.findIndex((t) => String(t.id) === String(srcTask.id));
        if (idx !== -1) {
          next[idx] = cloneTask(srcTask);
        } else {
          next.push(cloneTask(srcTask));
        }
      };

      if (String(task.id).startsWith('stack-')) {
        const src = combinedTasksRef.current.find(t => String(t.id) === String(task.id));
        if (src && src.shapes) {
          src.shapes.forEach(s => {
            const subId = String(s.id);
            let sIdx = next.findIndex(t => String(t.id) === subId);
            if (sIdx === -1) {
              const dummy = {
                ...task,
                id: subId,
                name: s.params?.Reference || s.type,
                progress: task.progress
              };
              next.push(dummy);
            } else {
              const subClone = cloneTask(next[sIdx]);
              subClone.progress = task.progress;
              next[sIdx] = subClone;
            }
          });
        }
      }

      ensureTask(task);
      updateTasks(next);
    },
    [updateTasks],
  );

  /**
   * onDelete(task)
   * Fires when user presses Delete on a selected bar.
   * Return false to cancel the deletion.
   */
  const handleDelete = useCallback(
    (task) => {
      if (
        !window.confirm(
          t('gantt.confirm_delete', {
            name: task.name,
            defaultValue: `Delete "${task.name}"?`,
          }),
        )
      ) {
        return false;
      }
      const next = tasksRef.current
        .filter((x) => x.id !== task.id)
        .map((x) => ({
          ...x,
          dependencies: (x.dependencies || []).filter(
            (d) => d !== task.id,
          ),
          project: x.project === task.id ? '' : x.project,
        }));
      updateTasks(next);
      return true;
    },
    [updateTasks, t],
  );

  /** onClick(task) - scroll timeline to task start */
  const handleClick = useCallback((task) => {
    setViewDate(new Date(task.start));
  }, []);

  /** onDoubleClick(task) - open inline name edit */
  const handleDoubleClick = useCallback((_task) => {
    // Double-click no longer needed; use focus on inputs
  }, []);

  /** onSelect(task, isSelected) - visual selection */
  const handleSelect = useCallback((_task, _isSelected) => { }, []);

  /**
   * onExpanderClick(task)
   * Toggle hideChildren for project/group tasks.
   * This is the official gantt-task-react API for expand/collapse.
   */
  const handleExpanderClick = useCallback(
    (task) => {
      if (task.id.startsWith('group_')) {
        setExpandedShapeGroups((prev) => {
          const next = new Set(prev);
          if (next.has(task.id)) {
            next.delete(task.id);
          } else {
            next.add(task.id);
          }
          return next;
        });
      } else {
        const next = tasksRef.current.map((t) =>
          t.id === task.id
            ? { ...t, hideChildren: !t.hideChildren }
            : t,
        );
        updateTasks(next);
      }
    },
    [updateTasks],
  );

  // =========================================================================
  // Inline editing & CRUD
  // =========================================================================

  const onCommitEdit = useCallback((overrideTaskId, overrideField, overrideValue) => {
    const taskId = overrideTaskId || editingCell?.taskId;
    const field = overrideField || editingCell?.field;
    const value = overrideValue !== undefined ? overrideValue : editValue;

    if (!taskId || !field) return;
    let next = [...tasksRef.current];

    const srcTask = combinedTasksRef.current.find((t) => String(t.id) === String(taskId));
    if (!srcTask) return;

    let tIndex = next.findIndex((t) => String(t.id) === String(taskId));
    const isDynamicGroup = String(taskId).startsWith('group_');

    if (tIndex === -1 && !isDynamicGroup) {
      next.push(cloneTask(srcTask));
      tIndex = next.length - 1;
    }

    // Skip creating a persistence entry for a dynamic group if it doesn't exist yet
    if (tIndex === -1 && isDynamicGroup) {
      // If we are editing dates of a dynamic group, handle it via children
      if (field === 'start' || field === 'end') {
        // This is handled by a similar delta logic if needed, 
        // but gantt-task-react primarily uses handleDateChange for dragging.
        // For inline date editing of a group, we calculate the delta here.
        const d = parseDateStr(value);
        if (d) {
          const originalGroup = combinedTasksRef.current.find(t => t.id === taskId);
          const delta = (field === 'start' ? d.getTime() : originalGroup.start.getTime()) - originalGroup.start.getTime();

          const childrenToShift = combinedTasksRef.current.filter(t => t.project === taskId);
          childrenToShift.forEach(child => {
            const updatedChild = cloneTask(child);
            updatedChild.start = new Date(child.start.getTime() + delta);
            updatedChild.end = new Date(child.end.getTime() + delta);

            const cIdx = next.findIndex(t => String(t.id) === String(child.id));
            if (cIdx !== -1) next[cIdx] = updatedChild; else next.push(updatedChild);
            next = cascadeDependents(next, updatedChild.id);
          });
        }
      }
      setEditingCell(null);
      return;
    }

    const clone = cloneTask(tIndex !== -1 ? next[tIndex] : srcTask);

    switch (field) {
      case 'name':
        clone.name = value.trim() || clone.name;
        break;
      case 'start': {
        const d = parseDateStr(value);
        if (d) {
          clone.start = d;
          // For regular tasks, ensure start < end
          if (clone.type !== 'milestone' && d >= clone.end) {
            clone.end = new Date(d);
            clone.end.setDate(clone.end.getDate() + 1);
          }
          // For milestones, keep them same
          else if (clone.type === 'milestone') {
            clone.end = new Date(d);
          }
        }
        break;
      }
      case 'end': {
        const d = parseDateStr(value);
        if (d) {
          clone.end = d;
          // For regular tasks, ensure end > start
          if (clone.type !== 'milestone' && d <= clone.start) {
            clone.start = new Date(d);
            clone.start.setDate(clone.start.getDate() - 1);
          }
          // For milestones, keep them same
          else if (clone.type === 'milestone') {
            clone.start = new Date(d);
          }
        }
        break;
      }
      case 'progress': {
        const val = parseInt(value, 10);
        clone.progress = Math.min(
          100,
          Math.max(0, isNaN(val) ? 0 : val),
        );
        break;
      }
      default:
        break;
    }

    if (clone.type === 'project' && (field === 'start' || field === 'end')) {
      const newGroupStart = clone.start.getTime();
      const newGroupEnd = clone.end.getTime();

      const originalChildren = combinedTasksRef.current.filter(t => t.project === clone.id);
      originalChildren.forEach(origChild => {
        let updatedChild = { ...origChild, start: new Date(origChild.start), end: new Date(origChild.end) };
        let changed = false;
        const childStart = updatedChild.start.getTime();
        const childEnd = updatedChild.end.getTime();
        const duration = childEnd - childStart;

        if (childEnd > newGroupEnd) {
          updatedChild.end = new Date(newGroupEnd);
          updatedChild.start = new Date(newGroupEnd - duration);
          changed = true;
        }
        if (updatedChild.start.getTime() < newGroupStart) {
          updatedChild.start = new Date(newGroupStart);
          updatedChild.end = new Date(newGroupStart + duration);
          changed = true;
        }

        if (changed) {
          const cIdx = next.findIndex((t) => String(t.id) === String(updatedChild.id));
          if (cIdx !== -1) {
            next[cIdx] = cloneTask(updatedChild);
          } else {
            next.push(cloneTask(updatedChild));
          }
          next = cascadeDependents(next, updatedChild.id);
        }
      });
    }

    if (tIndex === -1) {
      next.push(clone);
      tIndex = next.length - 1;
    } else {
      next[tIndex] = clone;
    }

    next = cascadeDependents(next, clone.id);

    // If it's a stacked task, propagate the commit to all individual shapes
    if (String(taskId).startsWith('stack-')) {
      const src = combinedTasksRef.current.find(t => String(t.id) === String(taskId));
      if (src && src.shapes) {
        console.log(`[Gantt] Committing edit for stack ${taskId} to ${src.shapes.length} shapes`);
        src.shapes.forEach(s => {
          const subId = String(s.id);
          let sIndex = next.findIndex(t => String(t.id) === subId);
          if (sIndex === -1) {
            const dummy = { ...clone, id: subId };
            next.push(dummy);
            sIndex = next.length - 1;
          }
          const subClone = cloneTask(next[sIndex]);

          switch (field) {
            case 'name':
              subClone.name = clone.name;
              break;
            case 'start':
              subClone.start = new Date(clone.start);
              subClone.end = new Date(clone.end);
              break;
            case 'end':
              subClone.end = new Date(clone.end);
              break;
            case 'progress':
              subClone.progress = clone.progress;
              break;
          }
          next[sIndex] = subClone;
        });
      }
    }

    updateTasks(next);
    setEditingCell(null);
  }, [editingCell, editValue, updateTasks]);

  /** Change task type inline (task <-> project <-> milestone) */
  const handleChangeType = useCallback(
    (taskId, newType) => {
      let next = [...tasksRef.current];
      let tIndex = next.findIndex((t) => String(t.id) === String(taskId));

      if (tIndex === -1) {
        const srcTask = combinedTasksRef.current.find((t) => String(t.id) === String(taskId));
        if (!srcTask) return;
        next.push(cloneTask(srcTask));
        tIndex = next.length - 1;
      }

      const clone = cloneTask(next[tIndex]);
      clone.type = newType;
      if (newType === 'milestone') {
        clone.end = new Date(clone.start);
        clone.progress = 0;
      }
      if (newType === 'project') {
        clone.project = '';
      }
      clone.hideChildren = newType === 'project' ? clone.hideChildren : false;
      next[tIndex] = clone;

      const prev = tasksRef.current.find((t) => t.id === taskId);
      if (prev?.type === 'project' && newType !== 'project') {
        next = next.map((t) =>
          t.project === taskId ? { ...t, project: '' } : t,
        );
      }
      updateTasks(next);
    },
    [updateTasks],
  );

  /** Update dependencies for a task */
  const handleChangeDeps = useCallback(
    (taskId, deps) => {
      let next = [...tasksRef.current];
      let tIndex = next.findIndex((t) => String(t.id) === String(taskId));

      if (tIndex === -1) {
        const srcTask = combinedTasksRef.current.find((t) => String(t.id) === String(taskId));
        if (!srcTask) return;
        next.push(cloneTask(srcTask));
        tIndex = next.length - 1;
      }

      // Cycle detection
      for (const depId of deps) {
        if (hasCycle(next, taskId, depId)) {
          alert(t('gantt.cycle_detected', { defaultValue: 'Error: Circular dependency detected!' }));
          return;
        }
      }

      next[tIndex] = { ...cloneTask(next[tIndex]), dependencies: deps };
      updateTasks(next);
    },
    [updateTasks, t],
  );

  // -- Add Task ---------------------------------------------------------------
  const handleAddTask = useCallback(() => {
    const start = new Date();
    const end = new Date();
    end.setDate(start.getDate() + 5);
    const id = uid();
    const newTask = {
      id,
      name: t('gantt.new_task', { defaultValue: 'New Task' }),
      start,
      end,
      progress: 0,
      type: 'task',
      project: '',
      dependencies: [],
      hideChildren: false,
      isDisabled: false,
      styles: {},
      displayOrder: tasksRef.current.length,
    };
    updateTasks([...tasksRef.current, newTask]);
    setTimeout(() => {
      setEditingCell({ taskId: id, field: 'name' });
      setEditValue(newTask.name);
    }, 50);
  }, [updateTasks, t]);

  // -- Add Project Group ------------------------------------------------------
  const handleAddProject = useCallback(() => {
    const start = new Date();
    const end = new Date();
    end.setMonth(start.getMonth() + 1);
    const id = uid();
    const proj = {
      id,
      name: t('gantt.new_project', { defaultValue: 'New Group' }),
      start,
      end,
      progress: 0,
      type: 'project',
      project: '',
      dependencies: [],
      hideChildren: false,
      isDisabled: false,
      styles: {},
      displayOrder: tasksRef.current.length,
    };
    updateTasks([...tasksRef.current, proj]);
    setTimeout(() => {
      setEditingCell({ taskId: id, field: 'name' });
      setEditValue(proj.name);
    }, 50);
  }, [updateTasks, t]);

  // -- Add Milestone ----------------------------------------------------------
  const handleAddMilestone = useCallback(() => {
    const d = new Date();
    const id = uid();
    const ms = {
      id,
      name: t('gantt.new_milestone', { defaultValue: 'Milestone' }),
      start: d,
      end: new Date(d),
      progress: 0,
      type: 'milestone',
      project: '',
      dependencies: [],
      hideChildren: false,
      isDisabled: false,
      styles: {},
      displayOrder: tasksRef.current.length,
    };
    updateTasks([...tasksRef.current, ms]);
    setTimeout(() => {
      setEditingCell({ taskId: id, field: 'name' });
      setEditValue(ms.name);
    }, 50);
  }, [updateTasks, t]);

  // -- Duplicate (without deps to avoid confusion) ----------------------------
  const handleDuplicateTask = useCallback(
    (task) => {
      const dup = cloneTask(task);
      dup.id = uid();
      dup.name = `${task.name} (copy)`;
      dup.dependencies = [];
      const idx = tasksRef.current.findIndex((x) => x.id === task.id);
      const next = [...tasksRef.current];
      next.splice(idx + 1, 0, dup);
      updateTasks(next);
    },
    [updateTasks],
  );

  // -- Delete from table -----------------------------------------------------
  const handleDeleteFromTable = useCallback(
    (task) => {
      if (
        !window.confirm(
          t('gantt.confirm_delete', {
            name: task.name,
            defaultValue: `Delete "${task.name}"?`,
          }),
        )
      )
        return;
      const next = tasksRef.current
        .filter((x) => x.id !== task.id)
        .map((x) => ({
          ...x,
          dependencies: (x.dependencies || []).filter(
            (d) => d !== task.id,
          ),
          project: x.project === task.id ? '' : x.project,
        }));
      updateTasks(next);
    },
    [updateTasks, t],
  );

  // -- DnD row reorder --------------------------------------------------------
  const handleReorder = useCallback(
    (oldIndex, newIndex) => {
      const display = buildDisplayTasks(tasksRef.current);
      const movedId = display[oldIndex]?.id;
      const targetId = display[newIndex]?.id;
      if (!movedId || !targetId) return;

      const masterOld = tasksRef.current.findIndex((t) => t.id === movedId);
      const masterNew = tasksRef.current.findIndex((t) => t.id === targetId);
      if (masterOld === -1 || masterNew === -1) return;

      const reordered = arrayMove(tasksRef.current, masterOld, masterNew);
      const withOrder = reordered.map((t, i) => ({
        ...t,
        displayOrder: i,
      }));
      updateTasks(withOrder);
    },
    [updateTasks],
  );

  // -- DnD drop into group ----------------------------------------------------
  const handleDropIntoGroup = useCallback(
    (taskId, projectId) => {
      const next = tasksRef.current.map((t) => {
        if (t.id === taskId) return { ...t, project: projectId };
        return t;
      });
      updateTasks(next);
    },
    [updateTasks],
  );

  const scrollToToday = useCallback(() => setViewDate(new Date()), []);

  const viewModes = useMemo(
    () => [
      {
        value: ViewMode.Hour,
        label: t('gantt.hour', { defaultValue: 'Hour' }),
      },
      {
        value: ViewMode.QuarterDay,
        label: t('gantt.quarter_day', { defaultValue: 'Quarter Day' }),
      },
      {
        value: ViewMode.HalfDay,
        label: t('gantt.half_day', { defaultValue: 'Half Day' }),
      },
      {
        value: ViewMode.Day,
        label: t('gantt.day', { defaultValue: 'Day' }),
      },
      {
        value: ViewMode.Week,
        label: t('gantt.week', { defaultValue: 'Week' }),
      },
      {
        value: ViewMode.Month,
        label: t('gantt.month', { defaultValue: 'Month' }),
      },
      {
        value: ViewMode.Year,
        label: t('gantt.year', { defaultValue: 'Year' }),
      },
    ],
    [t],
  );

  // Memoize custom components to prevent recreation on every render
  const renderTaskListHeader = useCallback(
    (props) => (
      <GanttTaskListHeader
        {...props}
        onAddTask={handleAddTask}
        t={t}
      />
    ),
    [handleAddTask, t],
  );

  const renderTaskListTable = useCallback(
    (props) => (
      <GanttTaskListTable
        {...props}
        onDeleteTask={handleDeleteFromTable}
        onDuplicateTask={handleDuplicateTask}
        onChangeType={handleChangeType}
        onChangeDeps={handleChangeDeps}
        allTasks={tasks}
        editingCell={editingCell}
        setEditingCell={setEditingCell}
        editValue={editValue}
        setEditValue={setEditValue}
        onCommitEdit={onCommitEdit}
        onReorder={handleReorder}
        onDropIntoGroup={handleDropIntoGroup}
        expandedRowId={expandedRowId}
        setExpandedRowId={setExpandedRowId}
        t={t}
        i18n={i18n}
      />
    ),
    [
      handleDeleteFromTable,
      handleDuplicateTask,
      handleChangeType,
      handleChangeDeps,
      tasks,
      editingCell,
      editValue,
      onCommitEdit,
      handleReorder,
      handleDropIntoGroup,
      expandedRowId,
      t,
      i18n,
    ],
  );

  // =========================================================================
  // Render
  // =========================================================================

  if (!authUser) {
    return (
      <div className="gantt-container">
        <p>
          {t('gantt.sign_in_required', {
            defaultValue:
              'Please sign in to access Gantt Planning.',
          })}
        </p>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="gantt-container">
        <p>
          {t('gantt.no_organization', {
            defaultValue:
              'No organization found. Please contact your administrator.',
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="gantt-container">
      {/* Header */}
      <header className="gantt-header">
        <h2>{t('gantt.title', { defaultValue: 'Gantt Planning' })}</h2>        <div className="gantt-header-controls">
          {/* PDF Toolbar style controls */}
          <div className="gantt-control-group">
            <label htmlFor="gantt-groupby">{t('inventory.group_by_param_title', { defaultValue: 'Group By' })}</label>
            <select
              id="gantt-groupby"
              value={groupingKey}
              onChange={(e) => setGroupingKey(e.target.value)}
              title={t('inventory.group_by_param_title')}
            >
              <option value="">{t('inventory.no_grouping', { defaultValue: 'None' })}</option>
              <option value="__page__">{t('inventory.page_label', { defaultValue: 'Page' })}</option>
              <option value="Assembly">{t('inventory.assembly_label', { defaultValue: 'Assembly' })}</option>
              <option value="Color">{t('inventory.color_label', { defaultValue: 'Color' })}</option>
              <option value="Reference">{t('inventory.reference_label', { defaultValue: 'Reference' })}</option>
              <option value="type">{t('inventory.type_label', { defaultValue: 'Type' })}</option>
            </select>
          </div>

          <div className="gantt-control-group">
            <input
              id="gantt-stack"
              className="gantt-checkbox"
              type="checkbox"
              checked={stackIdentical}
              onChange={(e) => setStackIdentical(e.target.checked)}
              title={t('inventory.stack_elements_tooltip')}
              style={{ margin: 0, cursor: 'pointer' }}
            />
            <label htmlFor="gantt-stack" style={{ margin: 0, cursor: 'pointer' }}>{t('inventory.stack_label', { defaultValue: 'Stack' })}</label>
          </div>

          <div className="gantt-control-group">
            <input
              id="gantt-page-sep"
              className="gantt-checkbox"
              type="checkbox"
              checked={separateByPage}
              onChange={(e) => setSeparateByPage(e.target.checked)}
              title={t('inventory.separate_by_page_title')}
              style={{ margin: 0, cursor: 'pointer' }}
            />
            <label htmlFor="gantt-page-sep" style={{ margin: 0, cursor: 'pointer' }}>{t('inventory.pg_sep_label', { defaultValue: 'Pg-Sep' })}</label>
          </div>

          <span className="gantt-separator" />

          {versions.length > 0 && (
            <div className="gantt-control-group">
              <button
                type="button"
                ref={versionTriggerRef}
                onClick={handleToggleVersionDropdown}
                className="gantt-btn gantt-btn-secondary"
                title={t('replan.pdfControls.select_versions')}
              >
                {selectedVersions.length === 0
                  ? t('inventory.select_a_version', { defaultValue: 'Select a version' })
                  : selectedVersions.length === 1
                    ? selectedVersions[0]
                    : `${selectedVersions.length} versions`}
                {loadingShapes ? ' ⏳' : ' ▾'}
              </button>
              {isVersionDropdownOpen && ReactDOM.createPortal(
                <div
                  ref={versionDropdownMenuRef}
                  className="takeoff-version-dropdown"
                  style={{
                    position: 'absolute',
                    top: versionDropdownPos.top,
                    left: versionDropdownPos.left,
                    zIndex: 9999,
                    background: 'var(--pico-dropdown-background-color)',
                    border: 'var(--pico-border-width) solid var(--pico-primary-border)',
                    borderRadius: 'var(--pico-border-radius)',
                    padding: '0.6rem',
                    minWidth: '220px',
                    boxShadow: 'var(--pico-box-shadow)'
                  }}
                >
                  <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.6rem', borderBottom: 'var(--pico-border-width) solid var(--pico-muted-border-color)', paddingBottom: '0.5rem' }}>
                    <button className="gantt-btn gantt-btn-secondary" style={{ height: '1.4rem', padding: '0 0.4rem', fontSize: '0.65rem' }} type="button" onClick={() => setSelectedVersions(versions)}>{t('replan.pdfControls.all')}</button>
                    <button className="gantt-btn gantt-btn-secondary" style={{ height: '1.4rem', padding: '0 0.4rem', fontSize: '0.65rem' }} type="button" onClick={() => setSelectedVersions([])}>{t('replan.pdfControls.none')}</button>
                    <button className="gantt-btn gantt-btn-secondary" style={{ height: '1.4rem', padding: '0 0.4rem', fontSize: '0.65rem', marginLeft: 'auto' }} type="button" onClick={() => setIsVersionDropdownOpen(false)}>❌</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: '200px', overflowY: 'auto' }}>
                    {versions.map((version) => (
                      <label key={version} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', margin: 0, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedVersions.includes(version)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...selectedVersions, version]
                              : selectedVersions.filter((v) => v !== version);
                            setSelectedVersions(next);
                          }}
                        />
                        {version}
                      </label>
                    ))}
                  </div>
                </div>,
                document.body
              )}
            </div>
          )}

          <div className="gantt-control-group">
            <select
              id="gantt-vm"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              title={t('gantt.view_mode')}
            >
              {viewModes.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>

          <button
            className={`gantt-btn gantt-btn-secondary ${showTaskList ? 'gantt-btn--active' : ''}`}
            onClick={() => setShowTaskList((v) => !v)}
            title={t('gantt.toggle_list', {
              defaultValue: 'Toggle task list',
            })}
          >
            ☰
          </button>

          <button
            className={`gantt-btn gantt-btn-secondary ${showTooltip ? 'gantt-btn--active' : ''}`}
            onClick={() => {
              setShowTooltip((v) => {
                const next = !v;
                tooltipEnabled = next;
                return next;
              });
            }}
            title={t('gantt.toggle_tooltip', {
              defaultValue: 'Toggle tooltip',
            })}
          >
            ⓘ
          </button>

          <button
            className="gantt-btn gantt-btn-secondary"
            onClick={scrollToToday}
            title={t('gantt.today', { defaultValue: 'Today' })}
          >
            ⊙
          </button>

          <span className="gantt-separator" />

          {/* History Controls */}
          <div className="gantt-control-group">
            <button
              className="gantt-btn gantt-btn-secondary"
              onClick={undo}
              disabled={!canUndo}
              title={t('inventory.undo_tooltip', { defaultValue: 'Undo (Ctrl+Z)' })}
            >
              ↶
            </button>
            <button
              className="gantt-btn gantt-btn-secondary"
              onClick={redo}
              disabled={!canRedo}
              title={t('inventory.redo_tooltip', { defaultValue: 'Redo (Ctrl+Y)' })}
            >
              ↷
            </button>
          </div>

          <span className="gantt-separator" />

          <button
            className={`gantt-btn ${baseline ? 'gantt-btn-secondary' : 'gantt-btn-secondary gantt-btn--outline'}`}
            onClick={handleSaveBaseline}
            disabled={saving || !selectedProject}
            title={t('gantt.save_baseline_tooltip', { defaultValue: 'Snapshot current dates as baseline' })}
          >
            📌 {t('gantt.baseline', { defaultValue: 'Baseline' })}
          </button>

          <span className="gantt-separator" />

          <div className="gantt-control-group">
            <button
              className="gantt-btn gantt-btn-primary"
              onClick={handleAddTask}
              disabled={!selectedProject}
              title={t('gantt.add_task')}
            >
              + {t('gantt.type_task', { defaultValue: 'Task' })}
            </button>
            <button
              className="gantt-btn gantt-btn-secondary"
              onClick={handleAddProject}
              disabled={!selectedProject}
              title={t('gantt.add_project')}
            >
              + {t('gantt.type_project', { defaultValue: 'Group' })}
            </button>
            <button
              className="gantt-btn gantt-btn-secondary"
              onClick={handleAddMilestone}
              disabled={!selectedProject}
              title={t('gantt.add_milestone')}
            >
              ◆ {t('gantt.type_milestone_short', { defaultValue: 'Mile.' })}
            </button>
          </div>

          <span className="gantt-separator" />

          <button
            className={`gantt-btn ${hasUnsavedChanges ? 'gantt-btn-primary' : 'gantt-btn-secondary'}`}
            onClick={handleManualSave}
            disabled={!hasUnsavedChanges || saving}
            title={hasUnsavedChanges ? t('gantt.unsaved_changes') : t('gantt.all_saved')}
          >
            {saving ? '⏳' : hasUnsavedChanges ? '💾' : '👍'}
            {hasUnsavedChanges && ` (${t('inventory.modified', { defaultValue: 'Modified' })})`}
          </button>
        </div>
      </header>

      {/* Chart */}
      <div className="gantt-chart-wrapper">
        {loading ? (
          <div className="gantt-loading">
            <p>
              {t('gantt.loading', {
                defaultValue: 'Loading planning data...',
              })}
            </p>
          </div>
        ) : displayTasks.length === 0 ? (
          <div className="gantt-empty">
            <p>
              {t('gantt.no_tasks', {
                defaultValue:
                  'No tasks yet \u2014 add one above to get started.',
              })}
            </p>
          </div>
        ) : (
          <Gantt
            tasks={displayTasks}
            viewMode={viewMode}
            viewDate={viewDate}
            locale={locale}
            rtl={false}
            preStepsCount={1}
            headerHeight={50}
            columnWidth={columnWidth}
            listCellWidth={showTaskList ? '735px' : '0'}
            rowHeight={44}
            barCornerRadius={4}
            barFill={65}
            handleWidth={10}
            fontSize="13px"
            fontFamily="var(--pico-font-family-sans-serif)"
            todayColor="rgba(59, 130, 246, 0.08)"
            arrowColor="var(--pico-muted-border-color)"
            arrowIndent={20}
            // Use colors that respect Pico CSS dark/light theme (as hex fallbacks)
            projectBackgroundColor="#41505e"
            projectBackgroundSelectedColor="#5d6d7c"
            projectProgressColor="#1095c1"
            projectProgressSelectedColor="#14b8ea"
            barProgressColor="#1095c1"
            barProgressSelectedColor="#14b8ea"
            barBackgroundColor="#2d3741"
            barBackgroundSelectedColor="#3a4552"
            milestoneBackgroundColor="#8b5cf6"
            milestoneBackgroundSelectedColor="#7c3aed"
            onDateChange={handleDateChange}
            onProgressChange={handleProgressChange}
            onDelete={handleDelete}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            onSelect={handleSelect}
            onExpanderClick={handleExpanderClick}
            timeStep={300000}
            TooltipContent={ControlledTooltip}
            TaskListHeader={renderTaskListHeader}
            TaskListTable={renderTaskListTable}
          />
        )}
      </div>
    </div>
  );
};

export default GanttPlanning;
